import { selectLatestComponent } from "@/lib/component-selection";
import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import type { AppProps } from "next/app";
import { ThemeProvider, CssBaseline, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { SnackbarProvider, closeSnackbar } from "notistack";
import { CacheProvider } from "@emotion/react";
import { appWithTranslation, useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import DashboardLayout from "@/components/Layout/DashboardLayout/DashboardLayout";
import Layout from "@/components/Layout/GeneralLayout/Layout";
import useAuthStore from "@/store/Auth/useAuthStore";
import useGeneralStore, {
  GeneralContentProvider,
} from "@/store/General/useGeneralStore";
import Head from "next/head";
import { useMemo } from "react";
import LogoLoader from "@/components/General/Loader";
import {
  AUTH_ROUTES,
  isMatchingRoute,
  Lang,
  LOGIN_ROUTES,
  MARKETING_ROUTES,
  UNDER_CONSTRUCTION_ROUTES,
} from "@/lib/auth.util";
import { Component } from "@/core/models/General.model";
//@ts-ignore
import "@styles/globals.css";
import "@lib/axios";
import createEmotionCache from "@/lib/createEmotionCache";
import AppThemeProvider from "@/lib/muiClasses";

const clientSideEmotionCache = createEmotionCache();
interface MyAppProps extends AppProps {
  emotionCache?: any;
  serverComponents?: {
    MetaComponent?: Component | null;
    BlogsComponent?: Component[] | null;
    HomeComponent?: Component | null;
    PlanComponent?: Component | null;
    AboutComponent?: Component | null;
    DonateComponent?: Component | null;
    ContactComponent?: Component | null;
    VolunteerComponent?: Component | null;
    SocialLinksComponent?: Component[] | null;
    EventsComponent?: Component | null;
    ServicesComponent?: Component | null;
  };
}

const App = ({
  Component,
  pageProps,
  emotionCache = clientSideEmotionCache,
  serverComponents,
}: MyAppProps) => {
  const router = useRouter();
  const { setCurrentLang, currentLang, setDirection, direction } = useAuthStore(
    (state) => state,
  );
  const { i18n } = useTranslation();
  const { isLoadingAllComponents, initializeComponents, fetchAllComponents } =
    useGeneralStore((state) => ({
      isLoadingAllComponents: state.isLoadingAllComponents,
      initializeComponents: state.initializeComponents,
      fetchAllComponents: state.fetchAllComponents,
    }));

  useEffect(() => {
    if (i18n?.dir) {
      Lang(i18n.language);
      setCurrentLang(i18n?.dir());
      setDirection(i18n?.dir());
    }
  }, [i18n.dir()]);

  useEffect(() => {
    if (serverComponents) initializeComponents(serverComponents);
    if (!serverComponents) void fetchAllComponents();
  }, [
    serverComponents,
    initializeComponents,
    fetchAllComponents,
    router.locale,
  ]);

  const initialContent = useMemo(
    () => ({
      ...serverComponents,
      ...(pageProps.article
        ? { BlogDetailComponent: pageProps.article }
        : { BlogDetailComponent: null }),
      isLoadingAllComponents: false,
      isLoadingBlogsComponent: false,
      isLoadingBlogDetailComponent: false,
    }),
    [serverComponents, pageProps.article],
  );
  const renderLayout = () => {
    const path = router.asPath.split("?")[0];

    if (
      isMatchingRoute(path, LOGIN_ROUTES) ||
      isMatchingRoute(path, UNDER_CONSTRUCTION_ROUTES)
    ) {
      return <Component {...pageProps} />;
    } else if (isMatchingRoute(path, AUTH_ROUTES)) {
      return (
        <DashboardLayout>
          <Component {...pageProps} />
        </DashboardLayout>
      );
    } else if (isMatchingRoute(path, MARKETING_ROUTES)) {
      return (
        <Layout>
          <Component {...pageProps} />
        </Layout>
      );
    } else {
      return <Component {...pageProps} />;
    }
  };

  if (isLoadingAllComponents && !serverComponents) {
    return (
      <CacheProvider value={emotionCache}>
        <AppThemeProvider>
          <CssBaseline />
          <LogoLoader fullScreen showBackdrop />
        </AppThemeProvider>
      </CacheProvider>
    );
  }

  return (
    <CacheProvider value={emotionCache}>
      <AppThemeProvider>
        <SnackbarProvider
          maxSnack={3}
          autoHideDuration={5000}
          SnackbarProps={{ dir: i18n.dir() }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: i18n.dir() === "rtl" ? "right" : "left",
          }}
          action={(snackbarId) => (
            <IconButton
              sx={{ color: "white" }}
              aria-label="close-snackbar"
              size="small"
              onClick={() => closeSnackbar(snackbarId)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        >
          <CssBaseline />
          <Head>
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1"
            />
          </Head>
          <GeneralContentProvider data={initialContent}>
            {renderLayout()}
          </GeneralContentProvider>
        </SnackbarProvider>
      </AppThemeProvider>
    </CacheProvider>
  );
};

App.getInitialProps = async (appContext: any) => {
  const { ctx } = appContext;
  const { locale, req } = ctx;

  const axios = require("axios");
  const protocol = req?.headers?.["x-forwarded-proto"] || "http";
  const host = req?.headers?.host;
  const baseUrl = host ? `${protocol}://${host}` : "";
  const API_URL =
    process.env.NEXT_PUBLIC_API_URL || (baseUrl ? `${baseUrl}/api` : "");

  const lang = locale || "ar";

  let serverComponents = {
    MetaComponent: null,
    BlogsComponent: null,
    HomeComponent: null,
    PlanComponent: null,
    AboutComponent: null,
    DonateComponent: null,
    ContactComponent: null,
    VolunteerComponent: null,
    SocialLinksComponent: null,
    EventsComponent: null,
    ServicesComponent: null,
    GovernoratesComponent: null,
  };

  if (API_URL) {
    const route = ctx.pathname || "/";
    const needed = new Set<string>();
    if (isMatchingRoute(route, MARKETING_ROUTES) || route === "/home") {
      needed.add("Home");
      needed.add("Meta");
      needed.add("SocialLinks");
    }
    if (route === "/news" || route.startsWith("/blogs")) needed.add("blogs");
    if (route.startsWith("/services")) needed.add("Services");
    if (route === "/about") needed.add("About");
    if (route === "/plan-your-visit") needed.add("PlanYourVisit");
    if (route === "/events") needed.add("Events");
    if (route === "/volunteer") needed.add("Volunteer");
    if (route === "/contact") needed.add("Contact");
    if (route.startsWith("/donate")) needed.add("Donate");
    try {
      const axiosInstance = axios.create({
        baseURL: API_URL,
        headers: {
          "Content-Type": "application/json",
          platform: "web",
          lang: lang,
        },
        timeout: 15000,
      });

      const fetchPageComponent = (type: string) =>
        needed.has(type)
          ? axiosInstance.get(`/General/GetComponent/${type}`)
          : Promise.resolve({ data: { data: null } });
      const [
        metaResponse,
        blogsResponse,
        homeResponse,
        planResponse,
        aboutResponse,
        donateResponse,
        contactResponse,
        volunteerResponse,
        socialLinksResponse,
        eventsResponse,
        servicesResponse,
        governoratesResponse,
      ] = await Promise.all(
        [
          fetchPageComponent("Meta"),
          fetchPageComponent("blogs"),
          fetchPageComponent("Home"),
          fetchPageComponent("PlanYourVisit"),
          fetchPageComponent("About"),
          fetchPageComponent("Donate"),
          fetchPageComponent("Contact"),
          fetchPageComponent("Volunteer"),
          fetchPageComponent("SocialLinks"),
          fetchPageComponent("Events"),
          fetchPageComponent("Services"),
          fetchPageComponent("Governorates"),
        ].map((request) => request.catch(() => ({ data: { data: null } }))),
      );
      const homeData = selectLatestComponent(homeResponse.data?.data, lang);
      if (needed.has("Home") && !homeData && ctx.res) {
        ctx.res.statusCode = 503;
        ctx.res.setHeader("Retry-After", "300");
      }
      const planData = selectLatestComponent(planResponse.data?.data, lang);
      const aboutData = selectLatestComponent(aboutResponse.data?.data, lang);
      const donateData = selectLatestComponent(donateResponse.data?.data, lang);
      const contactData = selectLatestComponent(
        contactResponse.data?.data,
        lang,
      );
      const volunteerData = selectLatestComponent(
        volunteerResponse.data?.data,
        lang,
      );
      const socialLinksData = Array.isArray(socialLinksResponse.data?.data)
        ? socialLinksResponse.data.data
        : socialLinksResponse.data?.data
          ? [socialLinksResponse.data.data]
          : [];
      const eventsData = selectLatestComponent(eventsResponse.data?.data, lang);
      const servicesData = selectLatestComponent(
        servicesResponse.data?.data,
        lang,
      );
      const governoratesData = selectLatestComponent(
        governoratesResponse.data?.data,
        lang,
      );
      serverComponents = {
        MetaComponent: selectLatestComponent(metaResponse.data?.data, lang),
        BlogsComponent: blogsResponse.data?.data || null,
        HomeComponent: homeData ?? null,
        PlanComponent: planData ?? null,
        AboutComponent: aboutData ?? null,
        DonateComponent: donateData ?? null,
        ContactComponent: contactData ?? null,
        VolunteerComponent: volunteerData ?? null,
        SocialLinksComponent:
          socialLinksData.length > 0 ? socialLinksData : null,
        EventsComponent: eventsData ?? null,
        ServicesComponent: servicesData ?? null,
        GovernoratesComponent: governoratesData ?? null,
      };
    } catch (error) {
      console.error("Error fetching components server-side:", error);
    }
  }

  return {
    serverComponents,
  };
};

export default appWithTranslation(App);
